import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import queryString from 'query-string';
import { navigate } from '@reach/router';

import { getDecodedId } from './ProductUtils';
import { getEncodedVariantId } from './VariantUtils';

/**
 * Save Visualizer
 *
 * Send mutation and wait for data to come back
 * once back get title from new visualizer
 * add to query params
 * if onSuccessFunc, call function
 * catch errors
 *
 * @param {gqlMutation} saveVisualizerMutation
 * @param {string} queryParams
 * @param {string} title
 * @param {Array} selectedProducts
 * @param {Func} onSuccessFunc
 * @param {Object} user
 * @return {null}
 */
export var saveVisualizer = function saveVisualizer(_ref) {
  var saveVisualizerMutation = _ref.saveVisualizerMutation,
      queryParams = _ref.queryParams,
      title = _ref.title,
      selectedProducts = _ref.selectedProducts,
      onSuccessFunc = _ref.onSuccessFunc,
      user = _ref.user;

  var slug = slugify(title);
  saveVisualizerMutation({
    variables: {
      userId: window.Dormify.customerId,
      url: getVisualizerUrl({ queryParams: queryParams, slug: slug }),
      title: title,
      slug: slug
    }
  }).then(function (_ref2) {
    var data = _ref2.data;

    var newVisualizer = data.addVisualizer.visualizers.find(function (v) {
      return v.visualizerTitle === title;
    });
    updateQueryParams({
      products: selectedProducts,
      queryParams: newVisualizer.visualizerLink.split('?')[1]
    });
    if (user && !user.visualizers.find(function (v) {
      return v.visualizerTitle === newVisualizer.title;
    })) {
      user.visualizers.push(newVisualizer);
    }
    if (onSuccessFunc) onSuccessFunc();
  }).catch(function (error) {
    // eslint-disable-next-line no-console
    console.log('error', error);
    alert('Failed to save, please try again', error);
  });
};

/**
 * Get Slug From Path Name
 *
 * Handle posibility of zaius using
 * trailing slashes.
 * This will use pathname and retrieve
 * the slug without a trailing slash
 *
 * @param {string} pathname
 * @return {string} slug
 */
export var getSlugFromPathName = function getSlugFromPathName(pathname) {
  return pathname.replace(/\/\s*$/, '').split('/').slice(-1)[0];
};

/**
 * Get Visualizer URL
 *
 * Adds title to queryParams if not already there
 * returns the relative url (pathname + updated query params)
 *
 * @param {string} queryParams
 * @param {string} slug
 * @return {string} visualizerUrl
 */
var getVisualizerUrl = function getVisualizerUrl(_ref3) {
  var queryParams = _ref3.queryParams,
      slug = _ref3.slug;

  var parsedQuery = parseQueryString(queryParams);
  parsedQuery.title = slug;

  return window.location.pathname + '?' + stringifyQueryParam(parsedQuery);
};

/**
 * Get Categories with Products
 *
 * Loop through category data and add products
 * that have the the same category
 * return categoryData with products included
 *
 * @param {Array} categoryData
 * @param {Array} products
 * @return {Array} Categories with Products
 */
export var getCategoriesWithProducts = function getCategoriesWithProducts(categoryData, products) {
  return categoryData.map(function (category) {
    var categoryProducts = products.filter(function (product) {
      return product.category === category.title;
    });
    category.products = categoryProducts;
    return category;
  });
};

/**
 * Add Product to Selection
 *
 * 1. Make copy of currently selected Products
 * 2. Get category or new product
 * 3. Get count of products in new product category
 * 4. If count is higher than max items, remove first prod of category.
 * 5. Add new product to selected products array
 *
 * @param {Array} selectedProducts
 * @param {Array} categories
 * @param {Object} product
 * @return {Array} newSelectedProducts
 */
export var addProductToSelection = function addProductToSelection(selectedProducts, categories, product) {
  var newSelectedProducts = [].concat(_toConsumableArray(selectedProducts));
  var cat = categories.find(function (category) {
    return category.title === product.category;
  });
  var categoryProdCount = newSelectedProducts.filter(function (prod) {
    return prod.category === product.category;
  }).length;
  if (categoryProdCount >= cat.maxItems) {
    var firstProd = newSelectedProducts.find(function (prod) {
      return prod.category === product.category;
    });
    newSelectedProducts = newSelectedProducts.filter(function (prod) {
      return prod.variantId !== firstProd.variantId;
    });
  }
  newSelectedProducts.push(product);
  return newSelectedProducts;
};

/**
 * Get Decorative Pillow Placement
 *
 * Decorative Pillows need even more placement data
 * based on amount of decorative pillows
 * return placement string
 *
 * @param {Object} product
 * @param {Array} selectedProducts
 * @return {String} placement
 */
var getDecorativePillowPlacement = function getDecorativePillowPlacement(product, selectedProducts, queryParams) {
  var placement = '';

  // 1. Get selected products
  var decorativePillows = selectedProducts.filter(function (products) {
    return products.category === 'decorative pillows';
  });

  // 2. Make an array of variant IDs from the selected pillows.
  // Get order clicked from params.
  var parsedQuery = parseQueryString(queryParams);
  var selectedVariantIds = getSelectedVariantIds(parsedQuery.selected);

  var selectedPillowVariantIds = selectedVariantIds.filter(function (id) {
    return decorativePillows.some(function (pillow) {
      return pillow.variantId == id;
    });
  });

  // 3. Get index based on id array.
  var productIndex = selectedPillowVariantIds.findIndex(function (variantId) {
    return variantId == product.variantId;
  });

  if (productIndex === 0) {
    placement = 'decorative-pillows__left';
  } else if (productIndex === 1) {
    placement = 'decorative-pillows__right';
  } else if (productIndex === 2) {
    placement = 'decorative-pillows__center';
  }
  return placement;
};

/**
 * Get Image Type
 *
 * Bed Visualizer images need extra data for placement
 * based on product data and image index, return a type string
 *
 * @param {Object} product
 * @param {Number} imgIndex
 * @param {Array} selectedProducts
 * @return {String} type
 */
var getImageType = function getImageType(product, imgIndex, selectedProducts, queryParams) {
  var categoryString = product.category.replace(/ /g, '-');
  var type = '';
  if (categoryString === 'decorative-pillows') {
    type = getDecorativePillowPlacement(product, selectedProducts, queryParams);
  } else if (imgIndex > 0) {
    type = categoryString + '-' + imgIndex;
  } else {
    type = categoryString;
  }
  return type;
};

/**
 * Get Bed Visualizer Images
 *
 * Loop through Selected Products
 * add src, type and alt text and add the array of images
 * return images
 *
 * @param {Array} selectedProducts
 * @return {Array} images
 */
export var getBedVisualizerImages = function getBedVisualizerImages(selectedProducts, queryParams) {
  var images = [];
  console.log('IMAGES: ', images);
  selectedProducts.map(function (product) {
    product.visualizerImages.map(function (img, imgIndex) {
      var type = getImageType(product, imgIndex, selectedProducts, queryParams);
      images.push({
        img: img,
        alt: product.category,
        type: type
      });
    });
  });
  console.log('IMAGES POST PUSH: ', images);
  return images;
};

/**
 * Get Selected Variant Ids
 *
 * 1. Normalize selectedQueryParamsArray (force it to be an array)
 *    [].concat.apply([],<input>) will force string or array to be array
 *
 *    while normalizing, remove empty strings and undefined values
 *      empty string = `?selected=` with no ids (no items selected)
 *      undefined = no query params
 *
 * 2. If selectedQueryParamsArray has values
 *      Loop through and get the encoded Variant ids
 *    Else
 *      Return empty array (no items selected)
 *
 * @param {Array or String or undefined} selectedQueryParams
 * @return {Array} images
 */
var getSelectedVariantIds = function getSelectedVariantIds(selectedQueryParams) {
  var selectedQueryParamsArray = [].concat.apply([], [selectedQueryParams].filter(function (p) {
    return p !== '' && p !== undefined;
  }));
  if (selectedQueryParamsArray.length) {
    return selectedQueryParamsArray.map(function (id) {
      return getEncodedVariantId(id);
    });
  } else {
    return [];
  }
};

/**
 * Get Selected Products from Query Param
 *
 * pass query params and product list
 * Steps
 * 1. Parse queryParams
 * 2. Get encoded Variant ID array
 *    a. if multiple items, map over and return encoded variant ids
 *    b. if single, return an array of a single encoded variant id
 *    c. else return empty array (no selected products)
 * 3. Filter product list with variant ids matching above array
 * 4. If parsedQuery.left,top, zIndex, also add position
 * 4. If encoded array has items
 *    a. check for discontinued products (variant ids that match none of the products)
 *    b. return the amount of discontinued items or 0
 * 4. Return { selected products, discontinued product count
 *
 * @param {String} queryParams
 * @param {Array} products
 * @return {Object} { selectedProducts<Array>, discontinuedProductCount<Number>}
 */
export var getSelectedProductsFromQueryParams = function getSelectedProductsFromQueryParams(queryParams, products) {
  var parsedQuery = parseQueryString(queryParams);

  var selectedVariantIds = getSelectedVariantIds(parsedQuery.selected);

  var selectedProducts = products.filter(function (prod) {
    return selectedVariantIds.includes(prod.variantId);
  });
  if (parsedQuery.left && parsedQuery.top && parsedQuery.zIndex) {
    selectedProducts = selectedProducts.map(function (prod) {
      var index = selectedVariantIds.indexOf(prod.variantId);
      return Object.assign({}, prod, {
        left: percentageToPixelCalc(normalizeParsedParams(parsedQuery.left)[index]),
        top: percentageToPixelCalc(normalizeParsedParams(parsedQuery.top)[index]),
        zIndex: normalizeParsedParams(parsedQuery.zIndex)[index],
        width: prod.imageWidth ? prod.imageWidth + '%' : '10%',
        img: prod.visualizerImages[0], // only use first image for wall
        alt: prod.parentTitle + ' Visualizer Image'
      });
    });
  }

  var discontinuedProductCount = selectedVariantIds.length > 0 // must have something selected for there to be an error
  ? selectedVariantIds.filter(function (id) {
    return !products.some(function (prod) {
      return prod.variantId === id;
    });
  }).length : 0;

  return { selectedProducts: selectedProducts, discontinuedProductCount: discontinuedProductCount };
};

/**
 * Get Title from Query Param
 *  get parsed query.
 * iterate through user.visualizers to see if a slug matches query slug
 * if so return the title of that visualizer.
 * This will only allow the user of said visualizer to see title,
 * as well as an update button
 *
 * @param {String} queryParams
 * @param {Object} user
 * @return {String | null}  visualizerTitle | null
 */
export var getTitleFromQueryParams = function getTitleFromQueryParams(queryParams, user) {
  var parsedQuery = parseQueryString(queryParams);
  if (!parsedQuery.title) return;
  var userVisualizer = user && user.visualizers.find(function (v) {
    return parseQueryString(v.visualizerLink.split('?').pop()).title === parsedQuery.title;
  });
  return userVisualizer && userVisualizer.visualizerTitle;
};

/**
 * Update Query Params
 *
 * Pass an array of products and the cuurent qury params
 * iterate over the array and get the decoded id of each variantId
 * (encoded ID has = signs which makes them not work for query params)
 * update selected attribute of params
 * and navigate to the new query `?<stringParams>
 *
 * @param {Array} products
 * @param {String} queryParams
 * @return void
 */
export var updateQueryParams = function updateQueryParams(_ref4) {
  var products = _ref4.products,
      queryParams = _ref4.queryParams;

  var parsedParams = parseQueryString(queryParams);
  if (products) {
    var decodedVariantIds = products.map(function (prod) {
      return getDecodedId(prod.variantId);
    });
    parsedParams.selected = decodedVariantIds;
  }
  var slug = getSlugFromPathName(window.location.pathname);
  if (slug === 'wall-visualizer') {
    var top = products.map(function (prod) {
      return prod.top ? pixelToPercentageCalc(prod.top) : getNewBoxPosition(normalizeParsedParams(parsedParams.top));
    });
    var left = products.map(function (prod) {
      return prod.left ? pixelToPercentageCalc(prod.left) : getNewBoxPosition(normalizeParsedParams(parsedParams.left));
    });
    var zIndex = products.map(function (prod) {
      return prod.zIndex ? prod.zIndex : getZIndexPlusOne(normalizeParsedParams(parsedParams.zIndex));
    });

    parsedParams.top = top;
    parsedParams.left = left;
    parsedParams.zIndex = zIndex;
  }
  var stringParams = stringifyQueryParam(parsedParams);

  navigate('?' + stringParams);
};

/**
 * Parse Query String
 *
 * Pass the query param string.
 * return parsed query string with comma formatting
 *
 * @param {String} string
 * @return {Object} parsed query string
 */
var parseQueryString = function parseQueryString(string) {
  return queryString.parse(string, {
    arrayFormat: 'comma'
  });
};

/**
 * Stringify Query Param
 *
 * Pass the query param object
 * return stringified query param with comma formatting
 *
 * @param {Object} query
 * @return {String} query string
 */
var stringifyQueryParam = function stringifyQueryParam(query) {
  return queryString.stringify(query, {
    arrayFormat: 'comma'
  });
};

/**
 * slugify!
 *
 * Pass a string,
 *
 * remove all the garbage you don't want
 * make a pretty slug that will work in url
 *
 * @param {String} string
 * @return {String} slugified string
 */
var slugify = function slugify(string) {
  // all sorts of junk users might put in the title
  var replaceThis = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
  var withThat = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
  var p = new RegExp(replaceThis.split('').join('|'), 'g');
  var emojis = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;

  return string.toString().toLowerCase().replace(/\s+/g, '-') // Replace spaces with -
  .replace(p, function (c) {
    return withThat.charAt(replaceThis.indexOf(c));
  }) // Replace special characters
  .replace(/&/g, '-and-') // Replace & with 'and'
  .replace(emojis, '-emoji-') // Replace emojis with '-'
  .replace(/[^\w\-]+/g, '') /* eslint-disable-line */ // Remove all non-word characters
  .replace(/\-\-+/g, '-') /* eslint-disable-line */ // Replace multiple - with single -
  .replace(/^-+/, '') // Trim - from start of text
  .replace(/-+$/, ''); // Trim - from end of text
};

/**
 * Get Thumbnail URL
 *
 * Pass the relativeUrl string.
 * return Thumbnail Url. (string with realitveUrl attached)
 *
 * @param {String} relativeUrl
 * @return {String} thumbnailUrl
 */
export var getThumbnailUrl = function getThumbnailUrl(_ref5) {
  var relativeUrl = _ref5.relativeUrl,
      _ref5$encodeDormifyUr = _ref5.encodeDormifyUrl,
      encodeDormifyUrl = _ref5$encodeDormifyUr === undefined ? false : _ref5$encodeDormifyUr;

  var dormifyUrl = encodeDormifyUrl ? encodeURIComponent('https://www.dormify.com' + relativeUrl) : 'https://www.dormify.com' + relativeUrl;
  return 'https://dormify.firebaseapp.com/api/thumbnailcapture/v1/thumbnail.png?url=' + dormifyUrl;
};

/**
 * Get Visualizer Noun
 *
 * return window.location.pathname === '/pages/wall-visualizer' ? `Wall` : `Bed`;
 * @return {String} Return noun based on url pathname
 */
export var getVisualizerNoun = function getVisualizerNoun() {
  var slug = getSlugFromPathName(window.location.pathname);
  return slug === 'wall-visualizer' ? 'Wall' : 'Bed';
};

/**
 * Check If Touch Device
 *
 * Return boolean whether device is a touch device
 * @return {Boolean} New z index
 */
export var checkIfTouchDevice = function checkIfTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints;
};

/**
 * Get Z Index Plus One
 *
 * Get the highest Z index of current boxes
 * Return that plus one so new item is on top of all previous boxes
 *
 * @param {Array} array
 * @return {Number} New z index
 */
export var getZIndexPlusOne = function getZIndexPlusOne(array) {
  return array.map(function (zIndex) {
    return zIndex ? zIndex : 1;
  }).reduce(function (a, b) {
    return Math.max(a, b);
  }, 0) + 1;
};

/**
 * Get New Box Position
 *
 * If we need a new box. pass in array of current box positions
 * Start at 0 and see if there is already an box at that position
 * loop through until you find a position which is currently not taken
 *
 * @param {Array} array
 * @return {Number} New Box Position
 */
export var getNewBoxPosition = function getNewBoxPosition(array) {
  var newBoxPosition = 0;
  do {
    newBoxPosition += 2;
  } while (array.includes(newBoxPosition));
  return newBoxPosition;
};

/**
 * Normalize Parsed Params
 *
 * Parsed query string can return a few different types of results
 * it can be a string, an array, and array of undefined values etc
 * This function will return an array of numbers so we can start calc
 *
 * @param {Array} array
 * @return {Array} Normalized Array of numbers
 */
export var normalizeParsedParams = function normalizeParsedParams(array) {
  return (Array.isArray(array) ? array : [array]).filter(function (item) {
    return item !== '' && item !== undefined;
  }).map(function (string) {
    return parseInt(string);
  });
};

/**
 * Pixel to Percentage Calc
 *
 * So we want the gallery to be responsive
 * React-DnD wants gallery to be in pixels
 * So we need to transfer drag and drop measurements
 * to percentage of gallery so we can add to the query params
 *
 * @param {Number} Pixels
 * @return {Number} percentage
 */
export var pixelToPercentageCalc = function pixelToPercentageCalc(pixel) {
  var visualizerWidth = document.querySelector('.visualizer').offsetWidth;
  var calculation = pixel * 100 / visualizerWidth;
  return parseFloat(calculation.toFixed(2));
};

/**
 * Percentage to Pixel Calc
 *
 * So we want the gallery to be responsive
 * React-DnD wants gallery to be in pixels
 * So we need to transfer the query params percentage
 * to pixels for React-Dnd to use
 *
 * @param {string} percentage
 * @return {Number} Pixels
 */
export var percentageToPixelCalc = function percentageToPixelCalc(percentage) {
  var visualizerWidth = document.querySelector('.visualizer').offsetWidth;
  var calculation = parseFloat(percentage) / 100 * visualizerWidth;
  return parseFloat(calculation.toFixed(2));
};

/**
 * Has Search Query
 *
 * This checks whether a string is present
 * in the URL's queries. I.e. we would use this
 * to check if 'staging=true' is present.
 * If present, we would allow users
 * to use the visualizer with sold out products.
 */

export var hasSearchQuery = function hasSearchQuery(queryString, string) {
  return queryString && queryString !== '' ? queryString.includes(string) : window.location.search.includes(string);
};